import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import { initialize, unload } from '../../store/scorm/scormPreview.js';
import { translate } from '../../helpers/translate.js';
import { launchTypes } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-loading.js'
import '../shared/sdb-status-message.js';

@customElement('sdb-scorm-preview-player')
class SdbScormPreviewPlayer extends connect(store)(LitElement) {

    @property({ type: String })
    accessor packageId = null;

    @property({ type: Number })
    accessor launchType = launchTypes.LAUNCHTYPE_REGULAR;

    @state()
    accessor initialized = false;

    @state()
    accessor finished = false;

    @state()
    accessor scormUrl = null;

    @state()
    accessor scormWindow = null;

    get scormWindowIsOpen() {
        return this.scormWindow && !this.scormWindow.closed;
    }

    constructor() {
        super();
        this.scormWindow = null;
    }

    stateChanged(state) {
        this.initialized = state.scormPreview.initialized;
        this.finished = state.scormPreview.finished;
        this.enabled = state.scormPreview.enabled;
        this.scormUrl = state.scormPreview.url;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                .scorm-frame {
                    width: 100%;
                    height: 100%;
                    border: none;
                    margin: 0;
                    padding: 0;
                    background: #f7f7f7;
                }

                .newwindow {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .container {
                    width: 70%;
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                    justify-content: space-between;
                }

                #start_button {
                    margin-top: 20px;
                }
            `,
        ];
    }

    render() {
        if (!this.initialized) {
            return html`<div class="content"><sdb-loading></sdb-loading></div>`;
        }

        if (!this.enabled) {
            return html`
                <div class="content">
                    <sdb-status-message label=${translate('scormPackageNotEnabled')}></sdb-status-message>
                </div>
            `;
        }

        if (this.launchType >= launchTypes.LAUNCHTYPE_NEWWINDOW) {
            return html`
                <div class="newwindow">
                    <div class="container">
                        <div class="message">
                            ${translate(this.scormWindowIsOpen
                                ? 'newWindowOpenedMessage'
                                : 'newWindowMessage')
                            }
                        </div>
                        <sdb-content-button
                            id="start_button"
                            label=${translate('startInNewWIndow')}
                            primary
                            ?hidden=${this.scormWindowIsOpen}
                            @click=${async () => await this._openNewWindow()}>
                        </sdb-content-button>
                    </div>
                </div>
            `;
        }

        return html`
            <iframe
                class="scorm-frame"
                src=${this.scormUrl}
                allowfullscreen="true"
                webkitallowfullscreen="true"
                mozallowfullscreen="true">
            </iframe>
        `;
    }

    async firstUpdated() {
        if (this.launchType < launchTypes.LAUNCHTYPE_NEWWINDOW) {
            await this._unregisterServiceWorkers();
        }
    }

    async updated(changes) {
        if (changes.has('packageId')) {
            if (this.scormWindowIsOpen) {
                this.scormWindow.close();
            }

            if (!this.packageId && !this.finished) {
                store.dispatch(unload()); // if close was triggered outside of scormplayer
            }

            if (this.packageId) {
                store.dispatch(initialize({ packageId: this.packageId }));
            }
        }

        if (changes.has('finished') && this.finished) {
            if (this.scormWindow && !this.scormWindow.closed) {
                this.scormWindow.close();
            }

            this.dispatchEvent(new CustomEvent('finish', {
                composed: true,
                bubble: true,
            }));
        }

        if (this.launchType >= launchTypes.LAUNCHTYPE_NEWWINDOW &&
            this.initialized &&
            this.enabled &&
            !this.finished &&
            !this.scormWindowIsOpen) {
            await this._openNewWindow();
        }
    }

    async _openNewWindow() {
        if (this.scormWindowIsOpen || !this.scormUrl || this.scormUrl === 'about:blank') {
            return;
        }

        await this._unregisterServiceWorkers();

        let options = '';
        if (this.launchType === launchTypes.LAUNCHTYPE_NEWCHROMELESSWINDOW) {
            const w = window.screen.availWidth;
            const h = window.screen.availHeight;
            options = `status=0,toolbar=0,menubar=0,resizable=yes,width=${w},height=${h},left=0,top=0`;
        }

        this.scormWindow = window.open(this.scormUrl, 'scormWindow', options);
    }

    async _unregisterServiceWorkers() {
        if ('serviceWorker' in navigator) {
            try {
                const regs = await navigator.serviceWorker.getRegistrations();
                for (const reg of regs) {
                    if (reg.scope.startsWith(window.location.origin + '/')) {
                        await reg.unregister();
                        console.log('[SCORM] Unregistered SW:', reg.scope);
                    }
                }

            } catch (err) {
                console.warn('[SCORM] Failed to unregister SW:', err);
            }
        }
    }
}
